/* eslint-disable react/prop-types */
/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { styled } from 'styled-components';

import felixImage from 'assets/img/founder-felix.webp';
import michaelImage from 'assets/img/founder-michael.webp';

import { Reveal, Stagger, StaggerItem } from '../components/Motion';
import { Container, Eyebrow, Section, Title, v2 } from '../v2';

const founders = [
  {
    name: 'Dr. Felix Hopf',
    role: 'Zahnarzt',
    image: felixImage,
    accent: v2.blue,
  },
  {
    name: 'Dr. Michael Hopf',
    role: 'Mathematiker',
    image: michaelImage,
    accent: v2.orange,
  },
];

const Wrapper = styled(Section)`
  background: ${v2.bg};
  border-top: 1px solid ${v2.border};
  overflow: hidden;

  /* Soft warm glow behind the portraits. */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    right: -10%;
    width: 60%;
    aspect-ratio: 1;
    transform: translateY(-50%);
    background: radial-gradient(
      circle,
      rgba(255, 159, 79, 0.1) 0%,
      rgba(30, 150, 252, 0.06) 40%,
      transparent 70%
    );
    pointer-events: none;
  }
`;

const Grid = styled(Container)`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(48px, 6vw, 96px);
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 22px;
`;

const Mission = styled.div`
  position: relative;
  margin-top: 10px;
  padding-left: 26px;

  &::before {
    content: '';
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 0;
    width: 2px;
    border-radius: 2px;
    background: linear-gradient(180deg, ${v2.blue}, ${v2.orange});
  }
`;

const MissionLabel = styled.div`
  font-family: ${v2.fontBody};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${v2.textFaint};
  margin-bottom: 12px;
`;

const MissionStatement = styled.p`
  font-family: ${v2.fontDisplay};
  font-size: clamp(1.35rem, 2.2vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.3;
  color: ${v2.text};
  margin: 0 0 16px;
`;

const MissionText = styled.p`
  font-family: ${v2.fontBody};
  font-size: clamp(1rem, 1.3vw, 1.1rem);
  line-height: 1.7;
  color: ${v2.textMuted};
  margin: 0;
  max-width: 540px;
`;

const People = styled(Stagger)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(16px, 2.4vw, 28px);

  /* Offset the second portrait for a bit of rhythm on large screens. */
  @media (min-width: 961px) {
    > *:nth-child(2) {
      margin-top: 56px;
    }
  }

  /* Keep the portraits compact once the section stacks. */
  @media (max-width: 960px) {
    max-width: 560px;
  }
`;

const Card = styled.figure`
  margin: 0;
  border-radius: 22px;
  border: 1px solid ${v2.border};
  background: ${v2.surface};
  overflow: hidden;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.35);
  transition:
    border-color 0.35s ease,
    transform 0.35s ease;

  &:hover {
    border-color: ${v2.borderStrong};
    transform: translateY(-4px);
  }
`;

const Photo = styled.div`
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: ${v2.bgElevated};

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: 50% 30%;
  }
`;

const Caption = styled.figcaption`
  padding: 18px 20px 20px;

  h3 {
    font-family: ${v2.fontDisplay};
    font-size: clamp(1rem, 1.5vw, 1.2rem);
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${v2.text};
    margin: 0 0 6px;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: ${v2.fontBody};
    font-size: 14px;
    font-weight: 500;
    color: ${v2.textMuted};

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${({ $accent }) => $accent};
      box-shadow: 0 0 10px ${({ $accent }) => $accent};
    }
  }

  @media (max-width: 480px) {
    padding: 14px 14px 16px;
  }
`;

const FoundersV2 = () => (
  <Wrapper id="gruender">
    <Grid>
      <Copy>
        <Reveal>
          <Eyebrow $accent="orange">Die Gründer</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <Title>
            Aus der Praxis.
            <br />
            <span className="grad">Für die Praxis.</span>
          </Title>
        </Reveal>
        <Reveal delay={0.2}>
          <Mission>
            <MissionLabel>Unsere Mission</MissionLabel>
            <MissionStatement>
              Die modernste Praxis für Dich und Deine Patienten.
            </MissionStatement>
            <MissionText>
              Als Zahnarzt und Mathematiker verbinden wir den Alltag in der
              Praxis mit der Präzision von Daten und KI. Unser Ziel: Abrechnung
              und Dokumentation, die vollständig, korrekt und ohne Mehraufwand
              für Dein Team entstehen.
            </MissionText>
          </Mission>
        </Reveal>
      </Copy>

      <People delay={0.15} gap={0.12}>
        {founders.map((founder) => (
          <StaggerItem key={founder.name}>
            <Card>
              <Photo>
                <img
                  src={founder.image}
                  alt={`${founder.name}, ${founder.role} und Gründer von FOXI`}
                  width="640"
                  height="640"
                  loading="lazy"
                />
              </Photo>
              <Caption $accent={founder.accent}>
                <h3>{founder.name}</h3>
                <span>{founder.role}</span>
              </Caption>
            </Card>
          </StaggerItem>
        ))}
      </People>
    </Grid>
  </Wrapper>
);

export default FoundersV2;
